import React, { useState } from "react";
import { Link } from "react-router-dom";
import { products } from "./utils";

const Shop = () => {
  const [hoveredId, setHoveredId] = useState(null);

  // Helper to format currency based on type
  const formatCurrency = (amount, currencyType) => {
    if (currencyType === 'INR') {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(amount);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <section style={styles.container}>
      <div style={styles.bgGlow1}></div>
      <div style={styles.bgGlow2}></div>

      <div style={styles.header}>
        <span style={styles.badge}>PREMIUM SERVICES</span>
        <h1 style={styles.heading}>Expert Crypto Consulting</h1>
        <p style={styles.subtitle}>
          Select a tailored session to accelerate your digital asset journey.
        </p>
      </div>

      <div style={styles.grid}>
        {products.map((product) => (
          <Link
            key={product.id}
            to={`/checkout/${product.id}`}
            style={styles.link}
            onMouseEnter={() => setHoveredId(product.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <article style={{
              ...styles.card,
              transform: hoveredId === product.id ? "translateY(-12px)" : "translateY(0)",
              boxShadow: hoveredId === product.id 
                ? "0 30px 60px -12px rgba(147, 51, 234, 0.2)" 
                : "0 10px 30px -10px rgba(0,0,0,0.1)"
            }}>
              <div style={styles.imageContainer}>
                <div
                  style={{
                    ...styles.image,
                    backgroundImage: `url(../../${product.image})`,
                    transform: hoveredId === product.id ? "scale(1.1)" : "scale(1)",
                  }}
                />
                <div style={styles.imageOverlay}></div>
              </div>

              <div style={styles.content}>
                <h2 style={styles.productName}>{product.name}</h2>
                <p style={styles.productDesc}>{product.summary}</p>

                <div style={styles.priceContainer}>
                  {/* Indian Price */}
                  <span style={styles.priceInr}>
                    {formatCurrency(product.priceIndian, 'INR')}
                  </span>
                  {/* International Price */}
                  <span style={styles.priceUsd}>
                    International: {formatCurrency(product.priceInternational, 'USD')}
                  </span>
                </div>

                <button style={{
                  ...styles.button,
                  background: hoveredId === product.id 
                    ? "linear-gradient(90deg, #7c3aed, #4f46e5)" 
                    : "linear-gradient(90deg, #4f46e5, #7c3aed)"
                }}>
                  Book Session
                </button>
              </div>
            </article>
          </Link>
        ))}
      </div>
    </section>
  );
};

const styles = {
  container: {
    padding: "100px 20px",
    maxWidth: "1200px",
    margin: "0 auto",
    position: "relative",
    fontFamily: "'Inter', sans-serif",
  },
  bgGlow1: {
    position: "absolute",
    top: "0", left: "10%",
    width: "400px", height: "400px",
    background: "radial-gradient(circle, rgba(124, 58, 237, 0.05) 0%, transparent 70%)",
    zIndex: -1,
  },
  bgGlow2: {
    position: "absolute",
    bottom: "10%", right: "10%",
    width: "400px", height: "400px",
    background: "radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)",
    zIndex: -1,
  },
  header: {
    textAlign: "center",
    marginBottom: "60px",
  },
  badge: {
    fontSize: "14px", // Adjusted for better hierarchy
    fontWeight: "800",
    color: "#7c3aed",
    letterSpacing: "2px",
    textTransform: "uppercase",
    background: "rgba(124, 58, 237, 0.1)",
    padding: "6px 16px",
    borderRadius: "20px",
    display: "inline-block",
    marginBottom: "16px",
  },
  heading: {
    fontSize: "clamp(32px, 5vw, 48px)",
    fontWeight: "900",
    color: "#0f172a",
    letterSpacing: "-1px",
    marginBottom: "12px",
  },
  subtitle: {
    fontSize: "18px",
    color: "#64748b",
    maxWidth: "600px",
    margin: "0 auto",
    lineHeight: "1.6",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "30px",
  },
  link: {
    textDecoration: "none",
  },
  card: {
    borderRadius: "24px",
    overflow: "hidden",
    background: "#ffffff",
    border: "1px solid #f1f5f9",
    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  imageContainer: {
    height: "220px",
    position: "relative",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    backgroundSize: "cover",
    backgroundPosition: "center",
    transition: "transform 0.5s ease",
  },
  imageOverlay: {
    position: "absolute",
    bottom: 0, left: 0, right: 0,
    height: "50%",
    background: "linear-gradient(to top, rgba(0,0,0,0.4), transparent)",
  },
  content: {
    padding: "30px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flexGrow: 1,
  },
  productName: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#1e293b",
    margin: 0,
  },
  productDesc: {
    fontSize: "15px",
    lineHeight: "1.6",
    color: "#64748b",
    margin: 0,
    minHeight: "45px",
  },
  priceContainer: {
    margin: "15px 0",
    padding: "15px 0",
    borderTop: "1px solid #f1f5f9",
    borderBottom: "1px solid #f1f5f9",
    textAlign: "center",
  },
  priceInr: {
    fontSize: "28px",
    fontWeight: "900",
    color: "#0f172a",
    display: "block",
  },
  priceUsd: {
    fontSize: "19px",
    fontWeight: "600",
    color: "#7c3aed",
    display: "block",
    marginTop: "4px",
  },
  button: {
    padding: "16px",
    fontSize: "16px",
    fontWeight: "700",
    borderRadius: "14px",
    border: "none",
    color: "white",
    cursor: "pointer",
    width: "100%",
    boxShadow: "0 10px 20px -5px rgba(79, 70, 229, 0.3)",
    transition: "all 0.3s ease",
    marginTop: "auto",
  },
};

export default Shop;